/**
 * cnpjService.js — Funções centralizadas para CNPJ alfanumérico (GMUD 5178)
 *
 * Suporta tanto CNPJ legado (somente dígitos) quanto alfanumérico (IN RFB 2.229/2024).
 * Regra: posições 1-12 podem conter [A-Z0-9], posições 13-14 são dígitos verificadores [0-9].
 */

/**
 * Normaliza CNPJ para formato canônico: remove separadores, converte para uppercase.
 * Entrada pode ser mascarada (12.ABC.345/01DE-35) ou canônica (12ABC34501DE35).
 * @param {string} cnpj
 * @returns {string} CNPJ canônico (14 chars, uppercase, sem separadores)
 */
export const cnpjCanonical = (cnpj) => {
  if (!cnpj) return "";
  return cnpj.replace(/[.\-/\s]/g, "").toUpperCase();
};

/**
 * Aplica máscara visual ao CNPJ: XX.XXX.XXX/XXXX-XX
 * @param {string} cnpj - CNPJ canônico ou mascarado
 * @returns {string} CNPJ formatado com máscara
 */
export const cnpjFormat = (cnpj) => {
  const c = cnpjCanonical(cnpj);
  return c.replace(
    /^([A-Z0-9]{2})([A-Z0-9]{3})([A-Z0-9]{3})([A-Z0-9]{4})([0-9]{2})$/,
    "$1.$2.$3/$4-$5"
  );
};

/**
 * Valida CNPJ: estrutura + dígitos verificadores (módulo 11 com charCodeAt - 48).
 * Aceita entrada mascarada ou canônica.
 * @param {string} cnpj
 * @returns {boolean}
 */
export const cnpjValidate = (cnpj) => {
  const c = cnpjCanonical(cnpj);

  if (c.length !== 14) return false;

  // Compatibilidade: mantém o fluxo legado para CNPJ apenas numérico.
  if (/^\d{14}$/.test(c)) {
    return cnpjValidateLegacyNumeric(c);
  }

  // Novo fluxo (alfanumérico): 12 posições [A-Z0-9] + 2 DVs numéricos.
  return cnpjValidateAlphanumeric(c);
};

const cnpjValidateLegacyNumeric = (c) => {
  if (/^(\d)\1+$/.test(c)) return false;

  let tamanho = c.length - 2;
  let numeros = c.substring(0, tamanho);
  const digitos = c.substring(tamanho);
  let soma = 0;
  let pos = tamanho - 7;

  for (let i = tamanho; i >= 1; i--) {
    soma += parseInt(numeros.charAt(tamanho - i), 10) * pos--;
    if (pos < 2) pos = 9;
  }

  let resultado = soma % 11 < 2 ? 0 : 11 - (soma % 11);
  if (resultado !== parseInt(digitos.charAt(0), 10)) return false;

  tamanho += 1;
  numeros = c.substring(0, tamanho);
  soma = 0;
  pos = tamanho - 7;

  for (let i = tamanho; i >= 1; i--) {
    soma += parseInt(numeros.charAt(tamanho - i), 10) * pos--;
    if (pos < 2) pos = 9;
  }

  resultado = soma % 11 < 2 ? 0 : 11 - (soma % 11);
  return resultado === parseInt(digitos.charAt(1), 10);
};

const cnpjValidateAlphanumeric = (c) => {
  if (!/^[A-Z0-9]{12}[0-9]{2}$/.test(c)) return false;
  if (/^(.)\1+$/.test(c)) return false;

  const charValue = (ch) => ch.charCodeAt(0) - 48;
  const weights1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  const weights2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

  let soma = 0;
  for (let i = 0; i < 12; i++) {
    soma += charValue(c.charAt(i)) * weights1[i];
  }
  const dv1 = soma % 11 < 2 ? 0 : 11 - (soma % 11);
  if (dv1 !== parseInt(c.charAt(12), 10)) return false;

  soma = 0;
  for (let i = 0; i < 13; i++) {
    soma += charValue(c.charAt(i)) * weights2[i];
  }
  const dv2 = soma % 11 < 2 ? 0 : 11 - (soma % 11);
  return dv2 === parseInt(c.charAt(13), 10);
};
